<script lang="ts">
  import Icon from '$shared/atoms/Icon/Icon.svelte';
  import { IconType } from '$shared/constants/IconType';
  import Spinner from '$shared/atoms/Spinner/Spinner.svelte';
  import { removeNonASCIICharacters } from '$shared/helpers/utils';
  import { InputType } from '$shared/constants/InputType';

  interface Props {
    type?: InputType | string;
    label?: string;
    icon?: string;
    name?: string;
    value?: string | number;
    error?: string;
    placeholder?: string;
    disabled?: boolean;
    tabIndex?: number;
    required?: boolean;
    loading?: boolean;
    max?: string;
    min?: string;
    maxlength?: string;
    readonly?: boolean;
    actions?: import('svelte').Snippet;
    onblur?: (e: FocusEvent) => void;
    onfocus?: (e: FocusEvent) => void;
    onchange?: (e: Event) => void;
    onclickicon?: () => void;
    onkeydown?: (e: KeyboardEvent) => void;
    oninput?: (detail: { value: string }) => void;
  }

  let {
    type = '',
    label = '',
    icon = '',
    name = '',
    value = $bindable(''),
    error = $bindable(''),
    placeholder = '',
    disabled = false,
    tabIndex = 1,
    required = true,
    loading = false,
    max = '',
    min = '',
    maxlength = '',
    readonly = false,
    actions,
    onblur,
    onfocus,
    onchange,
    onclickicon,
    onkeydown,
    oninput
  }: Props = $props();
  export const focus = () => inputRef.focus();
  export const blur = () => inputRef.blur();
  export const select = () => inputRef.select();

  let inputRef: HTMLInputElement = $state();

  const onClickIcon = () => {
    onclickicon?.();
  };

  const getMaxValue = () => {
    if (type === InputType.Date) {
      return '2999-12-31';
    }
    return max;
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    if (type === InputType.Number && (e.keyCode === 38 || e.keyCode === 40)) {
      e.preventDefault();
    }
    onkeydown?.(e);
  };

  const onInputChange = (e: any): void => {
    const value = removeNonASCIICharacters(e.target?.value);
    inputRef.value = value;
    oninput?.({ value });
  };
</script>

<div class="comp-input" class:error>
  {#if label}
    <label class="label" for={name}
      >{label}
      {#if required}
        <span class="required">*</span>
      {/if}
    </label>
  {/if}
  <div class="input-wrap">
    <input
      bind:this={inputRef}
      class:hasIcon={!!icon}
      type={type || InputType.Text}
      data-cy={name}
      {name}
      {value}
      {placeholder}
      {disabled}
      {tabIndex}
      {required}
      {maxlength}
      {readonly}
      autocomplete="off"
      max={getMaxValue()}
      {min}
      oninput={onInputChange}
      {onblur}
      {onfocus}
      {onchange}
      onkeydown={handleKeyDown}
    />
    {#if loading}
      <div class="icon">
        <Spinner />
      </div>
    {:else if icon}
      <div class="icon">
        <Icon name={icon} value={icon} {disabled} type={IconType.Button} onclick={onClickIcon} />
      </div>
    {/if}
    {@render actions?.()}
  </div>
  {#if error}
    <div class="error">{error}</div>
  {/if}
</div>

<style>
  .comp-input {
    width: 100%;
    display: flex;
    flex-direction: column;
    position: relative;
  }
  .comp-input .label {
    display: inline-block;
    font-weight: 600;
    font-size: 12px;
    margin-bottom: 8px;
  }
  .comp-input .label .required {
    position: relative;
    font-size: 14px;
    margin-left: 3px;
    color: var(--black);
  }

  .comp-input .input-wrap {
    display: flex;
    position: relative;
  }

  .input-wrap .icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 30px;
    height: 100%;
    position: absolute;
    top: 0;
    right: 0;
  }

  .comp-input input {
    width: 100%;
    height: 36px;
    line-height: 1;
    background: #ffffff;
    border: 1px solid rgba(0, 0, 0, 0.15);
    box-shadow: 1px 2px 2px rgba(0, 0, 0, 0.02);
    border-radius: 3px;
    padding: 0 8px;
    color: var(--grey-dark);
  }
  .comp-input input.hasIcon {
    padding-right: 30px;
  }
  .comp-input input:focus {
    outline-color: var(--blue);
  }
  .comp-input input:disabled {
    background-color: var(--grey-bg);
    cursor: not-allowed;
  }
  .comp-input.error input {
    border-color: var(--red);
  }

  .comp-input input::-webkit-outer-spin-button,
  .comp-input input::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  .comp-input input[type='number'] {
    -moz-appearance: textfield;
  }

  .comp-input .error {
    position: absolute;
    top: calc(100% + 3px);
    left: 3px;
    width: 100%;
    font-size: 12px;
    font-weight: 600;
    color: var(--red);
  }
</style>
